// Панель раздела «Предупреждения»: дата, роды кадра, список сообщений, рубежи.
//
// Список — сообщения, у которых хоть одна фигура легла в кадр: «где опасно
// здесь», а не перечень всей сводки на полмира. Цвет рода — полоса у края
// строки, тот же, что у зоны на карте: по нему строка и зона узнают друг друга.

import type { NavwarnMessage } from '../api'
import { formatDay, formatDayTime, useLang, useT } from '../i18n'
import { BoundaryToggles } from '../map/BoundaryToggles'
import { MapPanel } from '../map/MapPanel'
import { navwarnColor, navwarnKindKey, navwarnTitle } from '../map/navwarn'
import type { BoundaryMeta, BoundaryToggles as Toggles } from '../map/useBoundaryOverlays'
import type { MsgKey } from './navwarnState'
import { kindFacets, regionLabel, sameMsg } from './navwarnState'

interface Props {
  date: string
  today: string
  onDate: (d: string | null) => void
  messages: NavwarnMessage[]
  /** Все сообщения кадра — для фасетов (отбор их не урезает). */
  allMessages: NavwarnMessage[]
  kinds: string[]
  onToggleKind: (kind: string | null) => void
  selected: MsgKey | null
  onSelect: (m: NavwarnMessage) => void
  memoAsOf: string | null
  loading: boolean
  failed: boolean
  boundaries: Toggles
  onToggleBoundary: (key: keyof Toggles) => void
  boundaryMeta: BoundaryMeta
}

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar'

function KindChip({ kind, count, active, onClick, label }: {
  kind: string | null; count?: number; active: boolean; onClick: () => void; label: string
}) {
  const color = kind ? navwarnColor(kind) : null
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11.5px] leading-5 ${FOCUS} `
        + (active ? 'text-foam' : 'border-hull text-mist hover:border-mist hover:text-foam')}
      style={active ? { borderColor: color ?? undefined, background: color ? `${color}2e` : undefined } : undefined}>
      {color && <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />}
      <span>{label}</span>
      {count != null && <span className="tabular-nums text-faint">{count}</span>}
    </button>
  )
}

export function NavwarnPanel(p: Props) {
  const t = useT()
  const { lang } = useLang()
  const facets = kindFacets(p.allMessages)

  return (
    <MapPanel
      storageKey="navwarn.panel"
      className="w-[328px]"
      title={t('navwarn.title')}
      meta={<span title={t('navwarn.inFrame', { count: p.messages.length })}>{p.messages.length}</span>}
      footer={
        <div className="space-y-2">
          <BoundaryToggles on={p.boundaries} onToggle={p.onToggleBoundary} meta={p.boundaryMeta} />
          <p className="text-[10.5px] leading-snug text-faint">
            {p.memoAsOf && <>{t('navwarn.memos', { value: formatDayTime(lang, p.memoAsOf) })}. </>}
            {t('navwarn.disclaimer')}
          </p>
        </div>
      }
    >
      <div className="space-y-2.5 px-3 pb-2.5 text-[12px]">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-mist">
            <span>{t('navwarn.date')}</span>
            <input
              type="date"
              value={p.date}
              max={p.today}
              onChange={e => p.onDate(e.target.value && e.target.value !== p.today ? e.target.value : null)}
              className={`rounded-md border border-hull bg-deep px-2 py-0.5 tabular-nums text-foam ${FOCUS}`}
            />
          </label>
          {p.date !== p.today && (
            <button type="button" className={`rounded px-1 text-sonar hover:text-foam ${FOCUS}`} onClick={() => p.onDate(null)}>
              {t('navwarn.today')}
            </button>
          )}
        </div>

        {facets.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <KindChip kind={null} active={!p.kinds.length} onClick={() => p.onToggleKind(null)} label={t('navwarn.allKinds')} />
            {facets.map(f => (
              <KindChip key={f.kind} kind={f.kind} count={f.count} active={p.kinds.includes(f.kind)}
                onClick={() => p.onToggleKind(f.kind)} label={t(navwarnKindKey(f.kind))} />
            ))}
          </div>
        )}
      </div>

      <ul className="border-t border-hull-hair text-[12px]">
        {p.messages.map(m => {
          const on = sameMsg(p.selected, m)
          return (
            <li key={m.id} className="border-b border-hull-hair last:border-b-0">
              <button
                type="button"
                onClick={() => p.onSelect(m)}
                aria-current={on || undefined}
                className={`relative block w-full py-2 pl-4 pr-3 text-left ${FOCUS} ${on ? 'bg-haze' : 'hover:bg-haze/60'}`}
              >
                <span aria-hidden className={`absolute bottom-1.5 left-1.5 top-1.5 rounded-full ${on ? 'w-1' : 'w-[3px]'}`}
                  style={{ background: navwarnColor(m.kind) }} />
                <span className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-foam">{t(navwarnKindKey(m.kind))}</span>
                  {m.issued && <span className="shrink-0 tabular-nums text-faint">{formatDay(lang, m.issued)}</span>}
                </span>
                <span className="mt-0.5 flex flex-wrap gap-x-2 text-mist">
                  <span className="tabular-nums">{navwarnTitle(m)}</span>
                  {m.regions[0] && <span className="text-faint">{regionLabel(m.regions[0])}</span>}
                </span>
              </button>
            </li>
          )
        })}
        {!p.loading && !p.failed && p.messages.length === 0 && (
          <li className="px-3 py-3 text-faint">{t('navwarn.empty')}</li>
        )}
        {p.failed && <li className="px-3 py-3 text-coral">{t('navwarn.error')}</li>}
      </ul>
    </MapPanel>
  )
}
